import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import urls from '@src/@core/auth/jwt/urls'
import axios from 'axios'

const axiosAuth = () => axios.create({
    baseURL: urls.url,
    headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
        'Content-Type': 'application/json'
    }
})

export const getAllComments = createAsyncThunk(
    'appComments/getAllComments',
    async (params = {}, { rejectWithValue }) => {
        try {
            const instance = axiosAuth()
            const response = await instance.get('/customers/comments/', {
                params: {
                    page_size: params.page_size ?? 10,
                    p: params.page ?? 1,
                    search: params.search ?? '',
                    start_date: params.start_date ?? '',
                    end_date: params.end_date ?? '',
                    customer_id: params.customer_id
                }
            })
            return response.data
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const updateComment = createAsyncThunk(
    'appComments/updateComment',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const instance = axiosAuth()
            const response = await instance.patch(`/customers/comments/${id}/`, data)
            return response.data
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const deleteComment = createAsyncThunk(
    'appComments/deleteComment',
    async (id, { rejectWithValue }) => {
        try {
            const instance = axiosAuth()
            await instance.delete(`/customers/comments/${id}/`)
            return id
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const downloadCommentsExcel = createAsyncThunk(
    'appComments/downloadCommentsExcel',
    async (params = {}, { rejectWithValue }) => {
        try {
            const instance = axios.create({
                baseURL: urls.url,
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('token')
                }
            })

            const response = await instance.get('/customers/comments/download-excel/', {
                params: {
                    ...(params.search ? { search: params.search } : {}),
                    ...(params.start_date ? { start_date: params.start_date } : {}),
                    ...(params.end_date ? { end_date: params.end_date } : {}),
                    ...(params.customer_id ? { customer_id: params.customer_id } : {})
                },
                responseType: 'blob'
            })

            // Disparar la descarga del archivo
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })
            const url = window.URL.createObjectURL(blob)

            const today = new Date().toISOString().split('T')[0]
            const fileName = params.customer_id ? `comentarios_cliente_${params.customer_id}_${today}.xlsx` : `comentarios_clientes_${today}.xlsx`

            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)

            return true
        } catch (error) {
            // Parsear el error si vino como blob
            let errorMsg = 'Ocurrió un error al descargar el reporte.'
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text()
                    const json = JSON.parse(text)
                    errorMsg = json.detail || errorMsg
                } catch {
                    // si no es JSON, dejamos el mensaje genérico
                }
            } else if (error.response?.data?.detail) {
                errorMsg = error.response.data.detail
            }
            return rejectWithValue(errorMsg)
        }
    }
)

export const appCommentsSlice = createSlice({
    name: 'appComments',
    initialState: {
        allData: [],
        total: 0,
        loading: false,
        isFetching: false,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // .addCase(getAllComments.pending, (state) => {
            //     state.loading = true
            //     state.isFetching = true
            //     state.error = null
            // })
            .addCase(getAllComments.fulfilled, (state, action) => {
                const data = action.payload
                state.allData = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : [])
                state.total = typeof data?.count === 'number' ? data.count : state.allData.length
                state.loading = false
                state.isFetching = false
            })
            .addCase(getAllComments.rejected, (state, action) => {
                state.loading = false
                state.isFetching = false
                state.error = action.payload || 'Error al obtener comentarios'
            })
            .addCase(updateComment.fulfilled, (state, action) => {
                const updatedComment = action.payload
                state.allData = state.allData.map(comment => (comment.id === updatedComment.id ? updatedComment : comment)
                )
            })
            .addCase(deleteComment.fulfilled, (state, action) => {
                const deletedId = action.payload
                state.allData = state.allData.filter(comment => comment.id !== deletedId)
                state.total = Math.max(state.total - 1, 0)
            })
    }
})

export default appCommentsSlice.reducer